import { useState } from "react";
import { Box, Button, Card, Typography } from "@mui/material";
import { CheckCircle, AccessTime, Add } from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Subscription } from "../../Utils/Types";
import styles from "./PurchasePromoBanner.module.css";
import BuyHoursButton from "./BuyHoursButton";
import PaySchoolButton from "./PaySchoolButton";
import RenewSubscriptionButton from "./RenewSubscriptionButton";
import AddProductModal from "./AddProductModal";

interface PurchasePromoBannerProps {
  subscription: (Subscription & { _id?: string; expiredDate?: string }) | null;
  activeOrders?: any[];
  clientData?: any;
  onOrderAdded?: () => void;
}

export default function PurchasePromoBanner({
  subscription,
  activeOrders = [],
  clientData,
  onOrderAdded,
}: PurchasePromoBannerProps) {
  const [addProductOpen, setAddProductOpen] = useState(false);
  // Derived values
  const hasSubscription = !!subscription?._id;

  const daysRemaining = subscription?.subscriptionExpiresAt
    ? Math.ceil(
        (new Date(subscription.subscriptionExpiresAt).getTime() -
          new Date().getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  const renderAmount = (amount: number) => {
    if (amount == null || isNaN(amount)) return "-";
    return (
      amount.toLocaleString("ro-RO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) +
      " " +
      Vocabulary.currency
    );
  };

  const addProductButton = (
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={() => setAddProductOpen(true)}
      sx={{
        fontWeight: 700,
        padding: "7px 16px",
        borderRadius: "10px",
        textTransform: "none",
        letterSpacing: "0.4px",
        fontSize: "0.82rem",
        background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
        boxShadow: "0 6px 20px rgba(25, 118, 210, 0.25)",
        whiteSpace: "nowrap",
        width: { xs: "100%", sm: "auto" },
        "&:hover": {
          background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
          boxShadow: "0 10px 28px rgba(25, 118, 210, 0.35)",
          transform: "translateY(-2px)",
        },
        "&:active": {
          transform: "translateY(0)",
          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)",
        },
      }}
    >
      {Vocabulary.addNewProduct}
    </Button>
  );

  // If has active subscription
  if (hasSubscription) {
    return (
      <Card className={`${styles.bannerCard} ${styles.activeSubscription}`}>
        <Box className={styles.bannerContent}>
          <Box className={styles.mainContent}>
            <Box className={styles.topRow}>
              <Box className={styles.headerSection}>
                <Box className={`${styles.iconBox} ${styles.activeIcon}`}>
                  <CheckCircle className={styles.icon} />
                </Box>
                <Box className={styles.headerText}>
                  <Typography variant="h6" className={styles.title}>
                    {Vocabulary.activePlan}
                  </Typography>
                  <Typography variant="body2" className={styles.subtitle}>
                    {subscription.name}
                  </Typography>
                </Box>
              </Box>
              <Box className={styles.statsBox}>
                <Typography variant="body2" className={styles.daysLabel}>
                  {Vocabulary.daysRemaining}
                </Typography>
                <Typography className={styles.daysValue}>
                  {daysRemaining > 0 ? daysRemaining : "0"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className={styles.actionSection}>
            <RenewSubscriptionButton
              currentSubscription={subscription}
              clientData={clientData}
              className={styles.actionButton}
            />
          </Box>
        </Box>

        <AddProductModal
          open={addProductOpen}
          onClose={() => setAddProductOpen(false)}
          onSuccess={() => onOrderAdded?.()}
          clientData={clientData}
        />
      </Card>
    );
  }

  // If no subscription - show pending orders with remaining amounts
  return (
    <Card className={`${styles.bannerCard} ${styles.noSubscription}`}>
      <Box className={styles.bannerContent}>
        <Box className={styles.mainContent}>
          {/* Header row: icon+title on left, button on right (desktop) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <Box className={styles.headerSection}>
              <Box className={`${styles.iconBox} ${styles.noSubscriptionIcon}`}>
                <AccessTime className={styles.icon} />
              </Box>
              <Box className={styles.headerText}>
                <Typography variant="h6" className={styles.title}>
                  {Vocabulary.noSubscription}
                </Typography>
                <Typography variant="body2" className={styles.subtitle}>
                  {Vocabulary.noSubscriptionMessage}
                </Typography>
              </Box>
            </Box>

            {/* Button: right of title on desktop, full-width above orders on mobile */}
            <Box
              sx={{
                flexShrink: 0,
                alignSelf: { xs: "stretch", sm: "center" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                {addProductButton}
              </Box>
            </Box>
          </Box>

          {activeOrders?.length > 0 &&
            activeOrders.map((order: any) => {
              const totalAmount = Number(order?.product?.price);
              const remaining = totalAmount - (order.totalPaid || 0) || 0;
              return (
                <Box key={order._id} className={styles.orderItem}>
                  <Box className={styles.orderDetails}>
                    <Typography className={styles.productName}>
                      {order?.product?.name}
                    </Typography>
                    <Box className={styles.orderAmounts}>
                      <Typography className={styles.amountLabel}>
                        De plată:{" "}
                        <span className={styles.remainingValue}>
                          {renderAmount(remaining)}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={styles.orderItemButtons}>
                    {!order?.product?.isCertificate && (
                      <BuyHoursButton order={order} clientData={clientData} />
                    )}
                    <PaySchoolButton
                      order={order}
                      amountLeftToPay={remaining}
                      clientData={clientData}
                    />
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>

      <AddProductModal
        open={addProductOpen}
        onClose={() => setAddProductOpen(false)}
        onSuccess={() => onOrderAdded?.()}
        clientData={clientData}
      />
    </Card>
  );
}
